import React from 'react'
import { Container, Button, Text, Modal, Form, FormSerialization, Time, Spacer } from 'roadie-ui'
import { spendPSSTDays } from 'util/server/seattle'
import { SeattleData, PSSTDayRequest } from 'types/seattle'
import { useSelector } from 'react-redux'
import { TextMap } from 'util/translate'
import { useLocalization } from 'localization'

const ClaimPsst: React.FC<{
  psstDays: SeattleData['psstDays']
  claimedDays: SeattleData['seattleDays']
  onSubmit: VoidFunction
  staticTextMap: TextMap
}> = (props) => {
  const { claimedDays, psstDays, onSubmit, staticTextMap } = props
  const [modalOpen, setModalOpen] = React.useState(false)

  const handleModalOpen = React.useCallback(() => {
    setModalOpen(true)
  }, [])
  const handleModalClose = React.useCallback(() => {
    setModalOpen(false)
  }, [])
  const handleModalSubmit = React.useCallback(() => {
    onSubmit()
    setModalOpen(false)
  }, [onSubmit])

  return (
    <Container.Grid $justifyItems="center">
      <Button.Primary $padding="1rem 3.2rem" onClick={handleModalOpen}>
        {staticTextMap['Title']}
      </Button.Primary>
      <ClaimPsstModal
        open={modalOpen}
        claimedDays={claimedDays}
        psstDays={psstDays}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        staticTextMap={staticTextMap}
      />
    </Container.Grid>
  )
}

const ClaimPsstModal: React.FC<{
  open: boolean
  onClose: VoidFunction
  onSubmit: VoidFunction
  claimedDays: SeattleData['seattleDays']
  psstDays: SeattleData['psstDays']
  staticTextMap: TextMap
}> = (props) => {
  const { open, claimedDays, psstDays, onClose, onSubmit, staticTextMap } = props
  const { toLocalizedTime } = useLocalization()

  const profile = useSelector<any, any>((state) => state.profile)
  const [psstDaysLeft, setPsstDaysLeft] = React.useState<number>(psstDays.length)
  const [psstDaysToSpend, setPsstDaysToSpend] = React.useState(new Set<string>())
  const next7Days = React.useMemo(() => {
    const today = Time.now().startOfDay()

    return [
      today.clone(),
      today.tomorrow().clone(),
      today.tomorrow().clone(),
      today.tomorrow().clone(),
      today.tomorrow().clone(),
      today.tomorrow().clone(),
      today.tomorrow().clone(),
    ]
  }, [])
  const PSSTREASONS = [
    staticTextMap['Reason1'],
    staticTextMap['Reason2'],
    staticTextMap['Reason3'],
    staticTextMap['Reason4'],
    staticTextMap['Reason5'],
  ]
  const handlePsstDayChange = React.useCallback(
    (day: string) => (checked: boolean) => {
      const _psstDaysToSpend = new Set(psstDaysToSpend)
      checked ? _psstDaysToSpend.add(day) : _psstDaysToSpend.delete(day)
      setPsstDaysToSpend(_psstDaysToSpend)

      setPsstDaysLeft((psstDaysLeft) => psstDaysLeft + (checked ? -1 : 1))
    },
    [psstDaysToSpend]
  )
  const handleSubmit = React.useCallback(
    async (data: FormSerialization) => {
      const psstDaysIterator = psstDays.values()
      const psstDaysToSubmit: PSSTDayRequest[] = []
      psstDaysToSpend.forEach((psstDay) => {
        psstDaysToSubmit.push({
          id: psstDaysIterator.next().value.id,
          date_requested_off: psstDay,
          reason: data.psstReason,
        })
      })
      await spendPSSTDays(profile.id, psstDaysToSubmit)
      onSubmit()
    },
    [onSubmit, profile.id, psstDays, psstDaysToSpend]
  )

  return (
    <Modal.Basic title={staticTextMap['Title']} open={open} onRequestClose={onClose}>
      <Form.Basic
        validations={[
          ['psstDaysLeft', Form.validations.gte(0)],
          ['psstReason', Form.validations.required],
        ]}
        onSubmit={handleSubmit}
      >
        {next7Days.map((day) => {
          const isClaimed = (claimedDays.get(day) ?? {}).psstUsed
          const dayDate = day.toOffsetISOString()

          return (
            <React.Fragment key={`day-${dayDate}`}>
              <Text.Body2 $color={isClaimed ? 'neutral-dark' : 'neutral-black'}>
                <Form.Checkbox
                  name={`day-${dayDate}`}
                  label={`${toLocalizedTime(day.date, day.zone).format({
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}${isClaimed ? ` (${staticTextMap['PreviouslyRedeemed']})` : ''}`}
                  checked={isClaimed}
                  disabled={isClaimed}
                  onChange={handlePsstDayChange(dayDate)}
                />
              </Text.Body2>
            </React.Fragment>
          )
        })}
        <Spacer $height="1.6rem" />
        <Text.Body2 $textAlign="left" $color={psstDaysLeft < 0 ? 'error-base' : 'neutral-black'}>
          {staticTextMap['Label']}
          {psstDaysLeft}
        </Text.Body2>
        <Form.Input type="hidden" name="psstDaysLeft" value={String(psstDaysLeft)} />
        <Spacer $height="1.6rem" />
        <Form.Select name="psstReason" label={staticTextMap['Reason']}>
          {PSSTREASONS.map((reason) => (
            <option key={`psstReason-${reason}`} value={reason}>
              {reason}
            </option>
          ))}
        </Form.Select>
        <Spacer $height="2.4rem" />
        <Form.Submit>{staticTextMap['Submit']}</Form.Submit>
      </Form.Basic>
    </Modal.Basic>
  )
}

export default ClaimPsst
